<template>
  <ModuleHeader
    :title="$t('courses-management')"
    :sub_title="$t('create-lesson')"
  />

  <Container :is-loading="false">
    <template #content>
      <Form @submit="submit" :validation-schema="createLessonSchema">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div class="col-span-full my-4">
            <div class="flex items-center justify-start gap-5">
              <div
                class="col-span-1 rounded-2xl flex items-center justify-center overflow-hidden h-28 w-28 p-1 cursor-pointer primaryShadow"
              >
                <Image
                  :src="previewImage"
                  alt=""
                  class="object-cover h-24 w-24 bg-center rounded-xl"
                />
              </div>
              <div class="flex-grow flex flex-col gap-2">
                <div class="bg-dark200 rounded-xl h-6"></div>
                <label
                  for="photo"
                  class="block w-fit border-2 !text-base cursor-pointer border-primary rounded-2xl px-4 bg-white text-primaryDark py-2 align-middle leading-relaxed"
                >
                  {{ $t("upload-file") }}
                </label>
                <input
                  type="file"
                  name="photo"
                  id="photo"
                  @change="handleImageChange"
                  class="hidden"
                />
              </div>
            </div>
          </div>
          <div class="">
            <label for="title"> {{ $t("title") }} </label>
            <Field
              type="text"
              name="title"
              id="title"
              v-model="state.title"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            />
            <ErrorMessage class="error-message" name="title" />
          </div>
          <div class="">
            <label for="description"> {{ $t("description") }} </label>
            <Field
              as="textarea"
              name="description"
              id="description"
              v-model="state.description"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            />
            <ErrorMessage class="error-message" name="description" />
          </div>

          <div class="">
            <label for="order"> {{ $t("order") }} </label>
            <Field
              type="number"
              name="order"
              id="order"
              v-model="state.order"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            />
            <ErrorMessage class="error-message" name="order" />
          </div>

          <div class="">
            <label for="isPreview"> {{ $t("is-preview") }} </label>
            <Field
              as="select"
              name="isPreview"
              id="isPreview"
              v-model="state.isPreview"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            >
              <option :value="true">
                {{ $t("yes") }}
              </option>
              <option :value="false">
                {{ $t("no") }}
              </option>
            </Field>
            <ErrorMessage class="error-message" name="isPreview" />
          </div>

          <div class="my-4 col-span-full">
            <Alert
              type="danger"
              class="error-message"
              :message="errorMessage"
            />
          </div>

          <div class="flex items-center justify-start my-5 col-span-full gap-5">
            <PrimaryButton
              :disabled="false"
              :loading="false"
              type="button"
              :title="$t('cancel')"
              @click="resetForm"
              class="button-secondary !w-fit"
            />

            <PrimaryButton
              type="submit"
              class="!bg-primary !w-fit"
              :disabled="store.loading"
              :title="$t('save-changes')"
              :loading="store.loading"
            />
          </div>
        </div>
      </Form>
    </template>
  </Container>
</template>

<script lang="ts" setup>
import { Field, Form, ErrorMessage } from "vee-validate";
import { reactive, ref, onMounted } from "vue";
import { useCourse } from "../store/courseStore";
import { useRoute, useRouter } from "vue-router";
import { createLessonSchema } from "../validation/createLessonSchema";
import { CreateLessonRequestDto } from "../models/createLessonRequestDto";
import { UpdateLessonRequestDto } from "../models/updateLessonRequstDto";

const store = useCourse();
const router = useRouter();
const route = useRoute();

const courseId = route.params.course_id as string;
const chapterId = route.params.chapter_id as string;
const lessonId = route.params.lesson_id as string;

const errorMessage = ref<string>("");
const previewImage = ref<string>("");

const state = reactive<UpdateLessonRequestDto>({
  title: "",
  description: "",
  chapterId: route.params.chapter_id as string,
  courseId: route.params.course_id as string,
  lessonId: route.params.lesson_id as string,
  isPreview: false,
  order: 0,
  thumbnail: null,
});

onMounted(() => {
  store.fetchLesson(courseId, chapterId, lessonId).then((lesson) => {
    state.title = lesson.title;
    state.description = lesson.description;
    state.isPreview = lesson.isPreview;
    state.order = lesson.order;
    previewImage.value = lesson.photo;
  });
});

const handleImageChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  state.thumbnail = file;

  previewImage.value = URL.createObjectURL(file);
};

const submit = async () => {
  try {
    await store.updateLesson(courseId, chapterId, lessonId, state);
    router.back();
  } catch (error: any) {
    console.log(error);

    errorMessage.value = error?.response?.data?.message;
  }
};

const resetForm = () => {
  state.title = "";
  state.description = "";
  state.chapterId = route.params.chapter_id as string;
  state.courseId = route.params.course_id as string;
  state.isPreview = false;
  state.order = 0;
  state.thumbnail = null;
};
</script>
